'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { APP_CONFIG } from '@/config/app';

interface ReviewSection {
  id: string;
  title: string;
  status: 'approved' | 'pending' | 'flagged';
  fields: Array<{ label: string; value: string; aiConfidence?: number; editable?: boolean }>;
  aiNote?: string;
}

interface FilingReview {
  id: string;
  type: string;
  entityName: string;
  preparedAt: string;
  governmentFee: string;
  professionalFee: string;
  estimatedDays: string;
  sections: ReviewSection[];
  legalDisclaimer: string;
}

const mockReview: FilingReview = {
  id: 'REV-2026-0042',
  type: 'Private Limited Company Incorporation',
  entityName: 'Elysium Fragrances Private Limited',
  preparedAt: '2026-08-22',
  governmentFee: '₹1,500',
  professionalFee: '₹0 (AI-prepared)',
  estimatedDays: '7–10 working days',
  legalDisclaimer:
    'This application has been prepared by AI based on information you provided. By approving, you confirm that all details are accurate to the best of your knowledge. Submission to MCA constitutes a legal declaration. Incorrect information may result in rejection or legal liability.',
  sections: [
    {
      id: 'entity',
      title: 'Entity Details',
      status: 'approved',
      aiNote: 'Name availability confirmed. No conflicts found in MCA database.',
      fields: [
        { label: 'Proposed Company Name', value: 'Elysium Fragrances Private Limited', aiConfidence: 98 },
        { label: 'Entity Type', value: 'Private Limited Company', aiConfidence: 100 },
        { label: 'Main Object', value: 'Manufacturing and sale of perfumes, fragrances, and allied products', aiConfidence: 94 },
        { label: 'Authorised Capital', value: '₹10,00,000', aiConfidence: 100 },
        { label: 'Paid-up Capital', value: '₹1,00,000', aiConfidence: 100 },
      ],
    },
    {
      id: 'address',
      title: 'Registered Office',
      status: 'approved',
      fields: [
        { label: 'Address Line 1', value: '42, Sector 18, Gurugram', aiConfidence: 100 },
        { label: 'City', value: 'Gurugram', aiConfidence: 100 },
        { label: 'State', value: 'Haryana', aiConfidence: 100 },
        { label: 'PIN Code', value: '122015', aiConfidence: 100 },
      ],
    },
    {
      id: 'directors',
      title: 'Directors & Shareholders',
      status: 'flagged',
      aiNote: 'DIN for Director 2 is pending verification. Application can proceed but DIN must be obtained before filing.',
      fields: [
        { label: 'Director 1 — Name', value: 'Priya Sharma', aiConfidence: 100 },
        { label: 'Director 1 — DIN', value: '08234567', aiConfidence: 100 },
        { label: 'Director 1 — Shareholding', value: '60%', aiConfidence: 100 },
        { label: 'Director 2 — Name', value: 'Rahul Mehta', aiConfidence: 100 },
        { label: 'Director 2 — DIN', value: 'Pending', aiConfidence: 40, editable: true },
        { label: 'Director 2 — Shareholding', value: '40%', aiConfidence: 100 },
      ],
    },
    {
      id: 'documents',
      title: 'Documents Attached',
      status: 'approved',
      aiNote: 'All mandatory documents verified. Signatures collected via DigiLocker.',
      fields: [
        { label: 'MOA (Memorandum of Association)', value: 'AI-drafted · Verified', aiConfidence: 97 },
        { label: 'AOA (Articles of Association)', value: 'AI-drafted · Verified', aiConfidence: 97 },
        { label: 'Director 1 — Aadhaar', value: 'Verified via DigiLocker', aiConfidence: 100 },
        { label: 'Director 1 — PAN', value: 'Verified via DigiLocker', aiConfidence: 100 },
        { label: 'Director 2 — Aadhaar', value: 'Verified via DigiLocker', aiConfidence: 100 },
        { label: 'Registered Office Proof', value: 'Utility bill uploaded', aiConfidence: 95 },
      ],
    },
  ],
};

const statusConfig = {
  approved: { label: 'Approved', color: 'text-success bg-success/10 border-success/20' },
  pending: { label: 'Pending Review', color: 'text-warning bg-warning/10 border-warning/20' },
  flagged: { label: 'Needs Attention', color: 'text-danger bg-danger/10 border-danger/20' },
};

export default function ReviewPage() {
  const [sectionStates, setSectionStates] = useState<Record<string, 'approved' | 'pending' | 'flagged'>>(
    Object.fromEntries(mockReview.sections.map(s => [s.id, s.status]))
  );
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    Object.fromEntries(mockReview.sections.map(s => [s.id, true]))
  );

  const allApproved = Object.values(sectionStates).every(s => s === 'approved');
  const flaggedCount = Object.values(sectionStates).filter(s => s === 'flagged').length;

  const approveSection = (id: string) => {
    setSectionStates(prev => ({ ...prev, [id]: 'approved' }));
  };

  const handleSubmit = async () => {
    if (!agreed || !allApproved) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 2200));
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/review" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen flex items-center justify-center px-6">
          <div className="max-w-lg w-full text-center">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-success/20">
              <svg className="w-10 h-10 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-ink mb-3">Application Submitted</h1>
            <p className="text-ink-muted text-[14px] mb-2">
              Your application has been filed with MCA. Reference ID:
            </p>
            <p className="text-ai font-mono font-semibold text-[15px] mb-6">MCA-2026-{Math.floor(Math.random() * 900000 + 100000)}</p>
            <p className="text-[13px] text-ink-muted mb-8 leading-relaxed">
              You will receive status updates via email and in-app notifications. Estimated processing: {mockReview.estimatedDays}.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/dashboard" className="bg-ai text-white font-semibold px-6 py-2.5 rounded-xl text-[14px] hover:bg-ai-dark transition-colors">
                Go to Dashboard
              </Link>
              <Link href="/compliance" className="bg-surface-3 text-ink font-medium px-6 py-2.5 rounded-xl text-[14px] hover:bg-surface-2 border border-border transition-colors">
                View Compliance
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/review" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-8">

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 text-[12px] text-ink-muted mb-4">
              <Link href="/dashboard" className="hover:text-ai transition-colors">Dashboard</Link>
              <span>›</span>
              <span className="text-ink">Pre-Filing Review</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 bg-ai-light rounded-xl flex items-center justify-center border border-ai/20">
                    <svg className="w-5 h-5 text-ai" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-ink">Human Approval Gate</h1>
                    <p className="text-[12px] text-ink-muted">Review before AI files on your behalf</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {flaggedCount > 0 && (
                  <span className="text-[12px] font-semibold bg-danger/10 text-danger border border-danger/20 px-3 py-1.5 rounded-lg">
                    {flaggedCount} item{flaggedCount > 1 ? 's' : ''} need attention
                  </span>
                )}
                <span className="text-[11px] font-mono text-ink-subtle bg-surface-3 border border-border px-2.5 py-1.5 rounded-lg">
                  {mockReview.id}
                </span>
              </div>
            </div>
          </div>

          {/* Filing Summary Card */}
          <div className="bg-white border border-border rounded-2xl p-6 mb-6 shadow-card">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wider mb-1">Filing Type</p>
                <h2 className="text-[16px] font-bold text-ink">{mockReview.type}</h2>
                <p className="text-[13px] text-ink-muted mt-0.5">{mockReview.entityName}</p>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-[11px] text-ink-subtle mb-1">Gov. Fee</p>
                  <p className="text-[15px] font-bold text-ink">{mockReview.governmentFee}</p>
                </div>
                <div className="text-center">
                  <p className="text-[11px] text-ink-subtle mb-1">AI Fee</p>
                  <p className="text-[15px] font-bold text-success">{mockReview.professionalFee}</p>
                </div>
                <div className="text-center">
                  <p className="text-[11px] text-ink-subtle mb-1">Timeline</p>
                  <p className="text-[13px] font-semibold text-ink">{mockReview.estimatedDays}</p>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-5 pt-5 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[12px] font-medium text-ink-muted">Review progress</p>
                <p className="text-[12px] font-semibold text-ai">
                  {Object.values(sectionStates).filter(s => s === 'approved').length} / {mockReview.sections.length} sections approved
                </p>
              </div>
              <div className="w-full bg-surface-3 rounded-full h-2">
                <div
                  className="bg-ai h-2 rounded-full transition-all duration-500"
                  style={{ width: `${(Object.values(sectionStates).filter(s => s === 'approved').length / mockReview.sections.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-4 mb-6">
            {mockReview.sections.map(section => {
              const currentStatus = sectionStates[section.id];
              const cfg = statusConfig[currentStatus];
              const isExpanded = expandedSections[section.id];

              return (
                <div key={section.id} className={`bg-white border rounded-2xl overflow-hidden shadow-card transition-all ${
                  currentStatus === 'flagged' ? 'border-danger/30' : currentStatus === 'approved' ? 'border-success/20' : 'border-border'
                }`}>
                  {/* Section Header */}
                  <button
                    onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !isExpanded }))}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-surface-2/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        currentStatus === 'approved' ? 'bg-success' : currentStatus === 'flagged' ? 'bg-danger' : 'bg-warning'
                      }`} />
                      <h3 className="font-semibold text-ink text-[14px]">{section.title}</h3>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {currentStatus !== 'approved' && (
                        <button
                          onClick={e => { e.stopPropagation(); approveSection(section.id); }}
                          className="text-[12px] font-semibold text-success bg-success/10 border border-success/20 px-3 py-1 rounded-lg hover:bg-success/20 transition-colors"
                        >
                          Approve →
                        </button>
                      )}
                      <svg className={`w-4 h-4 text-ink-subtle transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5">
                      {section.aiNote && (
                        <div className={`flex items-start gap-2.5 p-3 rounded-xl mb-4 text-[12px] ${
                          currentStatus === 'flagged' ? 'bg-danger/5 border border-danger/15 text-danger' : 'bg-ai-light border border-ai/15 text-ai'
                        }`}>
                          <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="leading-relaxed">{section.aiNote}</span>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {section.fields.map((field, idx) => {
                          const fieldKey = `${section.id}-${idx}`;
                          const isEditing = editingField === fieldKey;
                          const displayValue = editValues[fieldKey] ?? field.value;

                          return (
                            <div key={idx} className="p-3.5 bg-surface-2 rounded-xl border border-border/60">
                              <div className="flex items-center justify-between mb-1.5">
                                <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide">{field.label}</p>
                                {field.aiConfidence !== undefined && (
                                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${
                                    field.aiConfidence >= 90 ? 'bg-success/10 text-success' :
                                    field.aiConfidence >= 70 ? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger'
                                  }`}>
                                    AI {field.aiConfidence}%
                                  </span>
                                )}
                              </div>
                              {isEditing ? (
                                <div className="flex gap-2">
                                  <input
                                    className="flex-1 text-[13px] font-medium text-ink bg-white border border-ai/40 rounded-lg px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-ai/20"
                                    value={displayValue}
                                    onChange={e => setEditValues(prev => ({ ...prev, [fieldKey]: e.target.value }))}
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => setEditingField(null)}
                                    className="text-[11px] font-semibold text-success bg-success/10 px-2.5 py-1.5 rounded-lg hover:bg-success/20 transition-colors"
                                  >
                                    Save
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between gap-2">
                                  <p className={`text-[13px] font-medium ${
                                    displayValue === 'Pending' ? 'text-danger' : 'text-ink'
                                  }`}>{displayValue}</p>
                                  {field.editable && (
                                    <button
                                      onClick={() => setEditingField(fieldKey)}
                                      className="text-[11px] text-ai hover:text-ai-dark transition-colors font-medium"
                                    >
                                      Edit
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {currentStatus !== 'approved' && (
                        <button
                          onClick={() => approveSection(section.id)}
                          className="mt-4 w-full py-2.5 bg-success/10 text-success border border-success/20 rounded-xl text-[13px] font-semibold hover:bg-success/20 transition-colors"
                        >
                          ✓ Approve this section
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legal Disclaimer + Submit */}
          <div className={`bg-white border rounded-2xl p-6 shadow-card transition-all ${allApproved ? 'border-ai/30' : 'border-border opacity-60'}`}>
            <h3 className="font-semibold text-ink text-[14px] mb-3">Legal Declaration</h3>
            <p className="text-[12px] text-ink-muted leading-relaxed mb-5 p-4 bg-surface-2 rounded-xl border border-border">
              {mockReview.legalDisclaimer}
            </p>

            <label className="flex items-start gap-3 cursor-pointer mb-6">
              <div
                onClick={() => allApproved && setAgreed(!agreed)}
                className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                  agreed ? 'bg-ai border-ai' : 'border-border bg-white'
                } ${!allApproved ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {agreed && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-[13px] text-ink-muted leading-relaxed">
                I have reviewed all sections above and confirm that the information is accurate. I authorise {APP_CONFIG.APP_NAME} to file this application on my behalf.
              </span>
            </label>

            <button
              onClick={handleSubmit}
              disabled={!allApproved || !agreed || submitting}
              className={`w-full py-3.5 rounded-xl font-semibold text-[14px] transition-all flex items-center justify-center gap-2 ${
                allApproved && agreed && !submitting
                  ? 'bg-ai text-white hover:bg-ai-dark shadow-sm hover:shadow-md'
                  : 'bg-surface-3 text-ink-subtle cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 0 12 0v4a8 8 0 00-8 8H4z" />
                  </svg>
                  Filing with MCA…
                </>
              ) : !allApproved ? (
                `Approve all ${mockReview.sections.length} sections to continue`
              ) : !agreed ? (
                'Check the declaration to continue'
              ) : (
                'Approve & File Application →'
              )}
            </button>

            {!allApproved && (
              <p className="text-center text-[11px] text-ink-subtle mt-3">
                {Object.values(sectionStates).filter(s => s !== 'approved').length} section(s) still need your approval
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
